import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FirebaseContext } from "../contexts/FirebaseContext";

// Owns the Detail tab's editable form state for a single task: it streams the
// task by id, mirrors title/description into local state, debounces writes back
// to Firestore (500ms), and handles deletion. Kept out of the screen component
// so the screen is just layout.
export function useTaskDetailForm(taskId) {
  const {
    taskDetail,
    startCollectingTaskById,
    updateTask,
    deleteTaskWithUndo,
    startCollectingLabels,
  } = useContext(FirebaseContext);
  const navigate = useNavigate();

  const [taskName, setTaskName] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const hasStartedCollection = useRef(false);
  const nameTimeoutRef = useRef(null);
  const descriptionTimeoutRef = useRef(null);

  useEffect(() => {
    if (!hasStartedCollection.current) {
      startCollectingTaskById(taskId);
      startCollectingLabels();
      hasStartedCollection.current = true;
    }
  }, [taskId, startCollectingTaskById, startCollectingLabels]);

  useEffect(() => {
    if (taskDetail) {
      setTaskName(taskDetail.title || "");
      setTaskDescription(taskDetail.description || "");
    }
  }, [taskDetail]);

  const debounceUpdateTask = (field, value, timeoutRef) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      updateTask(taskId, { [field]: value });
    }, 500);
  };

  const handleNameChange = (e) => {
    const newName = e.target.value;
    setTaskName(newName);
    debounceUpdateTask("title", newName, nameTimeoutRef);
  };

  const handleDescriptionChange = (e) => {
    const newDescription = e.target.value;
    setTaskDescription(newDescription);
    debounceUpdateTask("description", newDescription, descriptionTimeoutRef);
  };

  const handleDeleteTask = async () => {
    // No confirm dialog — delete immediately and offer Undo via snackbar,
    // matching the "Marked as done" flow.
    await deleteTaskWithUndo(taskDetail);
    navigate("/");
  };

  // Flush pending debounced writes on unmount. Copy refs into locals so the
  // cleanup reads the values captured at effect time.
  useEffect(() => {
    const nameRef = nameTimeoutRef;
    const descRef = descriptionTimeoutRef;
    return () => {
      if (nameRef.current) clearTimeout(nameRef.current);
      if (descRef.current) clearTimeout(descRef.current);
    };
  }, []);

  return {
    taskDetail,
    taskName,
    taskDescription,
    handleNameChange,
    handleDescriptionChange,
    handleDeleteTask,
  };
}
